import { colors as tokenColors } from "@plasma/tokens/theme";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { CreditCard } from "lucide-react";
import { useState } from "react";

import { env } from "~/env";
import { logger, PortalEvents } from "~/lib/logger";

import { Button } from "../ui/button";

/**
 * Stripe instance for the PLATFORM account.
 * Subscription billing happens on the platform account, not on connected accounts.
 * This way money goes to Plasma, not to the merchant.
 */
const stripePromise = loadStripe(env.PUBLIC_STRIPE_PUBLISHABLE_KEY);

interface PaymentSetupFormProps {
  onSuccess: () => void;
  onError: (error: Error) => void;
}

const PaymentSetupForm = ({ onSuccess, onError }: PaymentSetupFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!(stripe && elements)) {
      return;
    }

    setIsProcessing(true);
    logger.info(PortalEvents.PAYMENT_SETUP_STARTED);

    try {
      // Confirm the SetupIntent with the payment method details
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          // Return URL - user will be redirected here after setup completes
          return_url: `${window.location.origin}/onboarding?setup=success`,
        },
        redirect: "if_required", // Only redirect if required by payment method
      });

      if (error) {
        // Payment failed - show error message
        logger.error(PortalEvents.PAYMENT_SETUP_FAILED, {
          error_message: error.message,
          error_type: error.type,
        });
        onError(new Error(error.message ?? "Payment setup failed"));
      } else {
        // Payment method saved successfully
        logger.info(PortalEvents.PAYMENT_SETUP_COMPLETED);
        onSuccess();
      }
    } catch (err) {
      logger.exception(PortalEvents.PAYMENT_SETUP_FAILED, err);
      onError(err instanceof Error ? err : new Error("Payment setup failed"));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
      <div className="border-border rounded-lg border p-4">
        <PaymentElement />
      </div>

      <Button
        className="w-full"
        disabled={!stripe || isProcessing}
        size="lg"
        type="submit"
      >
        {isProcessing ? "Processing..." : "Save Payment Method"}
      </Button>
    </form>
  );
};

interface PaymentSetupProps {
  clientSecret: string;
  onSuccess: () => void;
  onError: (error: Error) => void;
}

export const PaymentSetup = ({
  clientSecret,
  onSuccess,
  onError,
}: PaymentSetupProps) => {
  if (!clientSecret) {
    return (
      <div className="bg-background flex max-w-md flex-col gap-5 rounded-lg p-6 text-center">
        <div className="flex flex-col gap-3">
          <CreditCard className="text-destructive mx-auto size-16" />
          <h1 className="h2 font-heading text-foreground text-balance">
            Unable to load payment form
          </h1>
          <p className="body text-foreground">
            Please try refreshing the page or contact support if the issue
            persists.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background flex max-w-md flex-col gap-5 rounded-lg p-6">
      <div className="flex flex-col gap-3 text-center">
        <CreditCard className="text-primary mx-auto size-16" />
        <div className="flex flex-col gap-2">
          <h1 className="h2 font-heading text-foreground text-balance">
            Add Payment Method
          </h1>
          <p className="body text-foreground">
            Your payment method will be securely saved for future billing.
          </p>
        </div>
      </div>

      <Elements
        options={{
          clientSecret,
          appearance: {
            theme: "stripe",
            variables: {
              colorPrimary: tokenColors.light.primary, // Primary brand color
              borderRadius: "8px",
            },
          },
        }}
        stripe={stripePromise}
      >
        <PaymentSetupForm onError={onError} onSuccess={onSuccess} />
      </Elements>

      <p className="body-sm text-muted-foreground text-center">
        Your information is encrypted and secure. You won't be charged until you
        start using the platform.
      </p>
    </div>
  );
};
